import { DatePipe } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, NgModule } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { MatOption } from "@angular/material/core";
import { MatFormField, MatLabel, MatSuffix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MatSelect } from "@angular/material/select";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from "@angular/material-moment-adapter";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule, RouterOutlet } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";

import { provideNgxMask } from 'ngx-mask';

import { environment } from "../environment/environment";

import { AutoLogoutModule } from "@app/core/auto-logout/auto-logout.module";
import { initClarity } from "@app/core/clarity/clarity-init";
import { AuthGuard } from "@app/core/guards";
import { REFRESH_BLACK_LIST_URL, REFRESH_BLACK_LIST_URLS } from "@app/core/injections/refresh-black-list-url.injection";
import { CorrelationIdInterceptor } from "@app/core/interceptors/correlation-id.interceptor";
import { HttpErrorInterceptor } from "@app/core/interceptors/http-error.interceptor";
import { LocaleInterceptor } from "@app/core/interceptors/localization.interceptor";
import { OrganizationInterceptor } from "@app/core/interceptors/organization.interceptor";
import { UnauthorizedInterceptor } from "@app/core/interceptors/unauthorized.interceptor";
import { LocalizationModule } from "@app/core/locale/localization.module";
import { AuthService } from "@app/core/services/auth.service";
import { ToastService } from "@app/core/services/toast.service";
import { TranslatedPaginatorIntl } from "@app/core/services/translated-paginator-intl";
import { NOTIFY_HUB_CONFIG } from "@app/core/signalR/signalr-injections";
import { TState } from "@app/core/types/state.type";
import { AuthEffects } from "@app/features/auth/store/auth.effects";
import { authFeature } from "@app/features/auth/store/auth.reducer";
import { PolicyEffects } from "@app/features/policies/store/policy.effects";
import { policyFeature } from "@app/features/policies/store/policy.reducer";
import { ToastComponent } from "@app/shared/components/toast/toast.component";
import {
  EmptyCurrencyToDashPipe,
  EmptyDateToDashPipe,
  EmptyDateWithoutTimeOrDash,
  EmptyValueToDashPipe,
} from "@app/shared/pipes/empty-value-to-dash.pipe";
import { IconAltPipe } from "@app/shared/pipes/icon-alt.pipe";
import { IconPathPipe } from "@app/shared/pipes/icon-path.pipe";
import { ThemeService } from "@app/shared/services/app-theme.service";
import { UnderUiComponent } from "@app/under-ui.component";
import { UnderUiRoutingModule } from "@app/under-ui-routing.module";
import { LogLevel } from "@microsoft/signalr";
import { EffectsModule } from "@ngrx/effects";
import { RootStoreConfig, StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { TranslateModule } from "@ngx-translate/core";

const STORE_CONFIG: RootStoreConfig<TState> = {
  runtimeChecks: {
    strictStateImmutability: true,
    strictActionImmutability: true,
    strictStateSerializability: true,
    strictActionSerializability: false,
  },
};

@NgModule({
  declarations: [UnderUiComponent, ToastComponent, IconPathPipe, IconAltPipe],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterOutlet,
    UnderUiRoutingModule,
    EffectsModule.forRoot([
      AuthEffects,
      PolicyEffects
    ]),
    StoreModule.forRoot<TState>(
      {
        auth: authFeature.reducer,
        policyState: policyFeature.reducer,
      },
      STORE_CONFIG
    ),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !environment.production
    }),
    LocalizationModule.forRoot(),
    AutoLogoutModule.forRoot(environment.autoLogoutConfig),
    MatFormField,
    MatIcon,
    MatLabel,
    MatOption,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    MatMomentDateModule,
    TranslateModule,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AuthGuard,
    ToastService,
    DatePipe,
    EmptyValueToDashPipe,
    EmptyDateToDashPipe,
    EmptyDateWithoutTimeOrDash,
    EmptyCurrencyToDashPipe,
    {
      provide: MatPaginatorIntl,
      useClass: TranslatedPaginatorIntl,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OrganizationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LocaleInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationIdInterceptor,
      multi: true,
    },
    {
      provide: REFRESH_BLACK_LIST_URL,
      useValue: REFRESH_BLACK_LIST_URLS,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (service: ThemeService) => () => service.initTheme(),
      deps: [ThemeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initClarity,
      multi: true,
    },
    {
      provide: NOTIFY_HUB_CONFIG,
      useFactory: (auth: AuthService) => ({
        hubUrl: `${environment.hubUrl}/notificationHub`,
        logLevel: LogLevel.Debug, // тут для теста по енваймкенту можна дивитись
        accessTokenFactory: () => auth.getAccessToken(),
      }),
      deps: [AuthService]
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: true }
    },
    provideNgxMask(),
  ],
  exports: [RouterModule],
  bootstrap: [UnderUiComponent],
})
export class UnderUiModule {}
